import React from "react";
import {
  formatStructuredAnalysis,
  getMarketTrendInfo,
  getConfidenceLevel,
} from "../../utils/analysisFormatter";

/**
 * Component hiển thị kết quả phân tích có cấu trúc (Structured Analysis Result)
 */
export const AnalysisResultSection = ({ analysis }) => {
  const structuredResult = formatStructuredAnalysis(
    analysis.analysis_result || analysis.result?.analysis_result
  );

  // Nếu không có thông tin, không render
  if (
    !structuredResult.price &&
    !structuredResult.marketTrend &&
    !structuredResult.summary &&
    structuredResult.marketInsights.length === 0 &&
    structuredResult.recommendations.length === 0
  ) {
    return null;
  }

  const trendInfo = getMarketTrendInfo(structuredResult.marketTrend);
  const confidenceInfo = getConfidenceLevel(structuredResult.confidence);

  return (
    <section className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-6 shadow-sm space-y-6">
      <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        <span className="text-lg">📊</span>
        Kết Quả Phân Tích
      </h4>

      {/* Price Information Cards */}
      {(structuredResult.price ||
        structuredResult.pricePerM2 ||
        structuredResult.marketTrend ||
        structuredResult.confidence) && (
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700 mb-3">
            💰 Thông Tin Giá
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Giá ước tính */}
            {structuredResult.price && (
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-300 rounded-lg p-4">
                <p className="text-xs uppercase text-emerald-700 font-bold mb-2">
                  Giá Ước Tính
                </p>
                <p className="text-2xl font-bold text-emerald-900">
                  {structuredResult.price}
                </p>
              </div>
            )}

            {/* Giá/m² */}
            {structuredResult.pricePerM2 && (
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-lg p-4">
                <p className="text-xs uppercase text-blue-700 font-bold mb-2">
                  Đơn Giá / m²
                </p>
                <p className="text-2xl font-bold text-blue-900">
                  {structuredResult.pricePerM2}
                </p>
              </div>
            )}

            {/* Xu hướng thị trường */}
            {structuredResult.marketTrend && (
              <div
                className={`bg-gradient-to-br rounded-lg p-4 border-2 ${
                  trendInfo.badge || "from-gray-50 to-gray-100 border-gray-300"
                }`}
              >
                <p className="text-xs uppercase font-bold mb-2">
                  Xu Hướng Thị Trường
                </p>
                <p className="text-xl font-bold flex items-center gap-2">
                  <span className="text-2xl">{trendInfo.icon}</span>
                  {trendInfo.label}
                </p>
              </div>
            )}

            {/* Độ tin cậy */}
            {structuredResult.confidence && (
              <div
                className={`bg-gradient-to-br rounded-lg p-4 border-2 ${
                  confidenceInfo.badge ||
                  "from-gray-50 to-gray-100 border-gray-300"
                }`}
              >
                <p className="text-xs uppercase font-bold mb-2">Độ Tin Cậy</p>
                <div className="flex items-center gap-2">
                  <p className="text-2xl">{confidenceInfo.icon}</p>
                  <div>
                    <p className="text-lg font-bold">
                      {structuredResult.confidence}%
                    </p>
                    <p className="text-sm">{confidenceInfo.level}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tóm tắt */}
      {structuredResult.summary && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">📝 Tóm Tắt</p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 leading-relaxed">
            {structuredResult.summary}
          </div>
        </div>
      )}

      {/* Nhận định thị trường */}
      {structuredResult.marketInsights.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            📈 Nhận Định Thị Trường
          </p>
          <div className="space-y-2">
            {structuredResult.marketInsights.map((insight, idx) => (
              <div
                key={idx}
                className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 flex items-start gap-3"
              >
                <span className="text-lg flex-shrink-0">💡</span>
                <p>{insight}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Khuyến nghị */}
      {structuredResult.recommendations.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">💬 Khuyến Nghị</p>
          <div className="space-y-2">
            {structuredResult.recommendations.map((rec, idx) => (
              <div
                key={idx}
                className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900 flex items-start gap-3"
              >
                <span className="text-lg flex-shrink-0">✅</span>
                <p>{rec}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default AnalysisResultSection;
